import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  Stack,
  Avatar,
  alpha,
  CircularProgress,
  Chip,
  Collapse,
  Badge,
} from '@mui/material';
import {
  Send as SendIcon,
  Close as CloseIcon,
  DeleteOutline as ClearIcon,
  SmartToy as BotIcon,
  Person as PersonIcon,
  KeyboardArrowDown as MinimizeIcon,
} from '@mui/icons-material';
import { agentAPI } from '../services/api';

// ─── Typing dots ─────────────────────────────────────────────────────────────
function TypingDots() {
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          sx={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            bgcolor: '#00d4ff',
            animation: 'fac-bounce 1.2s infinite',
            animationDelay: `${i * 0.2}s`,
            '@keyframes fac-bounce': {
              '0%, 80%, 100%': { transform: 'scale(0.55)', opacity: 0.35 },
              '40%': { transform: 'scale(1)', opacity: 1 },
            },
          }}
        />
      ))}
    </Stack>
  );
}

// ─── Message bubble ───────────────────────────────────────────────────────────
function Bubble({ msg }) {
  const isUser = msg.role === 'user';
  return (
    <Stack
      direction="row"
      spacing={1}
      justifyContent={isUser ? 'flex-end' : 'flex-start'}
      alignItems="flex-end"
      sx={{ mb: 1.25, px: 0.5 }}
    >
      {!isUser && (
        <Avatar sx={{ width: 26, height: 26, bgcolor: 'transparent', border: `1px solid ${alpha('#00d4ff', 0.4)}`, flexShrink: 0 }}>
          <BotIcon sx={{ fontSize: 14, color: '#00d4ff' }} />
        </Avatar>
      )}
      <Box
        sx={{
          maxWidth: '80%',
          px: 1.5,
          py: 1,
          borderRadius: isUser ? '14px 14px 3px 14px' : '14px 14px 14px 3px',
          background: isUser
            ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
            : alpha('#1a1f2e', 0.95),
          border: isUser ? 'none' : `1px solid ${alpha('#00d4ff', 0.15)}`,
          boxShadow: isUser ? '0 2px 8px rgba(118,75,162,0.3)' : '0 1px 4px rgba(0,0,0,0.3)',
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: isUser ? '#fff' : alpha('#fff', 0.88),
            fontSize: '0.82rem',
            lineHeight: 1.55,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {msg.text}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mt: 0.3,
            fontSize: '0.62rem',
            color: isUser ? alpha('#fff', 0.5) : alpha('#fff', 0.3),
            textAlign: isUser ? 'right' : 'left',
          }}
        >
          {msg.time}
        </Typography>
      </Box>
      {isUser && (
        <Avatar sx={{ width: 26, height: 26, bgcolor: alpha('#667eea', 0.2), border: `1px solid ${alpha('#667eea', 0.4)}`, flexShrink: 0 }}>
          <PersonIcon sx={{ fontSize: 14, color: '#667eea' }} />
        </Avatar>
      )}
    </Stack>
  );
}

const SUGGESTIONS = [
  "What were today's trades?",
  "Show me the P&L summary",
  "Is the system healthy?",
  "Which strategies fired today?",
];

// ─── Main widget ──────────────────────────────────────────────────────────────
export default function FloatingAgentChat() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [unread, setUnread] = useState(0);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setUnread(0);
      setTimeout(() => inputRef.current?.focus(), 150);
    }
  }, [open]);

  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading, open]);

  const now = () => new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  const send = async (text) => {
    const trimmed = (text || input).trim();
    if (!trimmed || loading) return;
    setInput('');
    setMessages((p) => [...p, { role: 'user', text: trimmed, time: now() }]);
    setLoading(true);

    const result = await agentAPI.chat(trimmed);
    setLoading(false);

    const reply = result.success
      ? result.reply
      : `Sorry, I couldn't reach the agent right now.\n\n${result.error || ''}${result.detail ? `\n\n${result.detail}` : ''}`;

    setMessages((p) => [...p, { role: 'agent', text: reply, time: now() }]);
    if (!open) setUnread((n) => n + 1);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  return (
    <Box sx={{ position: 'fixed', bottom: 28, right: 28, zIndex: 1400, display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>

      {/* ── Chat panel ──────────────────────────────────────────────────── */}
      <Collapse in={open} timeout={220} unmountOnExit>
        <Paper
          elevation={0}
          sx={{
            width: 360,
            height: 500,
            mb: 1.5,
            display: 'flex',
            flexDirection: 'column',
            bgcolor: '#0a0e14',
            border: `1px solid ${alpha('#00d4ff', 0.18)}`,
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 8px 40px rgba(0,0,0,0.6), 0 0 0 1px rgba(0,212,255,0.08)',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              px: 2,
              py: 1.25,
              borderBottom: `1px solid ${alpha('#00d4ff', 0.1)}`,
              background: 'linear-gradient(90deg, #0a0e14 0%, #0f1624 100%)',
              flexShrink: 0,
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box
                  sx={{
                    width: 30,
                    height: 30,
                    borderRadius: 1.5,
                    background: 'linear-gradient(135deg, #00d4ff 0%, #0066ff 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <BotIcon sx={{ fontSize: 17, color: '#fff' }} />
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ color: '#fff', fontWeight: 700, lineHeight: 1.2, fontSize: '0.85rem' }}>
                    TradeWave Agent
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#00ff88', boxShadow: '0 0 5px #00ff88' }} />
                    <Typography variant="caption" sx={{ color: alpha('#fff', 0.4), fontSize: '0.62rem' }}>
                      online
                    </Typography>
                  </Stack>
                </Box>
              </Stack>
              <Stack direction="row" spacing={0.25}>
                {messages.length > 0 && (
                  <IconButton size="small" onClick={() => setMessages([])} sx={{ color: alpha('#fff', 0.35), '&:hover': { color: '#ff4757' }, p: 0.5 }}>
                    <ClearIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                )}
                <IconButton size="small" onClick={() => setOpen(false)} sx={{ color: alpha('#fff', 0.35), '&:hover': { color: '#fff' }, p: 0.5 }}>
                  <MinimizeIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Stack>
            </Stack>
          </Box>

          {/* Messages */}
          <Box sx={{ flex: 1, overflowY: 'auto', py: 1.5, px: 0.5 }}>
            {messages.length === 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', px: 2, textAlign: 'center' }}>
                <Typography variant="body2" sx={{ color: alpha('#fff', 0.5), mb: 2, fontSize: '0.8rem' }}>
                  Ask me about trades, performance, system health, and more.
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, justifyContent: 'center' }}>
                  {SUGGESTIONS.map((s) => (
                    <Chip
                      key={s}
                      label={s}
                      size="small"
                      onClick={() => send(s)}
                      sx={{
                        bgcolor: alpha('#1a1f2e', 0.9),
                        color: alpha('#fff', 0.65),
                        border: `1px solid ${alpha('#00d4ff', 0.18)}`,
                        fontSize: '0.72rem',
                        cursor: 'pointer',
                        '&:hover': { bgcolor: alpha('#00d4ff', 0.12), color: '#fff', borderColor: alpha('#00d4ff', 0.4) },
                      }}
                    />
                  ))}
                </Box>
              </Box>
            ) : (
              <>
                {messages.map((msg, i) => <Bubble key={i} msg={msg} />)}
                {loading && (
                  <Stack direction="row" spacing={1} alignItems="flex-end" sx={{ mb: 1.25, px: 0.5 }}>
                    <Avatar sx={{ width: 26, height: 26, bgcolor: 'transparent', border: `1px solid ${alpha('#00d4ff', 0.4)}` }}>
                      <BotIcon sx={{ fontSize: 14, color: '#00d4ff' }} />
                    </Avatar>
                    <Paper elevation={0} sx={{ px: 1.5, py: 1, bgcolor: alpha('#1a1f2e', 0.95), border: `1px solid ${alpha('#00d4ff', 0.15)}`, borderRadius: '14px 14px 14px 3px' }}>
                      <TypingDots />
                    </Paper>
                  </Stack>
                )}
                <div ref={bottomRef} />
              </>
            )}
          </Box>

          {/* Input */}
          <Box sx={{ px: 1.25, py: 1.25, borderTop: `1px solid ${alpha('#00d4ff', 0.1)}`, flexShrink: 0 }}>
            <Stack direction="row" spacing={0.75} alignItems="flex-end">
              <TextField
                inputRef={inputRef}
                fullWidth
                multiline
                maxRows={3}
                placeholder="Ask anything…"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={loading}
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: alpha('#1a1f2e', 0.8),
                    borderRadius: 2,
                    color: '#fff',
                    fontSize: '0.82rem',
                    '& fieldset': { borderColor: alpha('#00d4ff', 0.18) },
                    '&:hover fieldset': { borderColor: alpha('#00d4ff', 0.35) },
                    '&.Mui-focused fieldset': { borderColor: '#00d4ff' },
                  },
                  '& .MuiInputBase-input::placeholder': { color: alpha('#fff', 0.28), fontSize: '0.82rem' },
                }}
              />
              <IconButton
                onClick={() => send()}
                disabled={!input.trim() || loading}
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 1.75,
                  background: !input.trim() || loading
                    ? alpha('#1a1f2e', 0.6)
                    : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: !input.trim() || loading ? alpha('#fff', 0.2) : '#fff',
                  flexShrink: 0,
                  '&:hover': { background: !input.trim() || loading ? alpha('#1a1f2e', 0.6) : 'linear-gradient(135deg, #7c91f5 0%, #8a5fbf 100%)' },
                  '&.Mui-disabled': { background: alpha('#1a1f2e', 0.6) },
                }}
              >
                {loading
                  ? <CircularProgress size={15} sx={{ color: alpha('#fff', 0.35) }} />
                  : <SendIcon sx={{ fontSize: 16 }} />}
              </IconButton>
            </Stack>
          </Box>
        </Paper>
      </Collapse>

      {/* ── FAB button ───────────────────────────────────────────────────── */}
      <Badge
        badgeContent={unread}
        color="error"
        invisible={open || unread === 0}
        sx={{ '& .MuiBadge-badge': { fontSize: '0.65rem', minWidth: 18, height: 18 } }}
      >
        <Box
          onClick={() => setOpen((v) => !v)}
          sx={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            background: open
              ? 'linear-gradient(135deg, #1a1f2e 0%, #0a0e14 100%)'
              : 'linear-gradient(135deg, #00d4ff 0%, #0066ff 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: open
              ? `0 4px 20px rgba(0,0,0,0.5), 0 0 0 2px ${alpha('#00d4ff', 0.3)}`
              : '0 4px 20px rgba(0,212,255,0.4), 0 2px 8px rgba(0,0,0,0.4)',
            transition: 'all 0.25s cubic-bezier(0.4,0,0.2,1)',
            '&:hover': {
              transform: 'scale(1.06)',
              boxShadow: open
                ? `0 6px 24px rgba(0,0,0,0.6), 0 0 0 2px ${alpha('#00d4ff', 0.5)}`
                : '0 6px 28px rgba(0,212,255,0.55)',
            },
            '&:active': { transform: 'scale(0.96)' },
          }}
        >
          {open
            ? <CloseIcon sx={{ color: '#00d4ff', fontSize: 24 }} />
            : <BotIcon sx={{ color: '#fff', fontSize: 26 }} />}
        </Box>
      </Badge>
    </Box>
  );
}
